import { useState, useMemo, useEffect } from "preact/hooks";
import "./record.scss";

export type SubmitChoice = "anon" | "email";

interface Props {
  audioBlob: Blob;
  onSubmit: (opts: { choice: SubmitChoice; email?: string }) => void;
  onReRecord: () => void;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(value);
}

/**
 * Preview + submission form shown after recording stops. Matches Figma frames
 * 1769 (desktop) and 3303 (phone): audio playback bar + radio choice between
 * anonymous (deletion code) and email + Re-record / Submit
 * actions. Classes live in record.scss.
 */
export function SubmissionForm({ audioBlob, onSubmit, onReRecord }: Props) {
  const [choice, setChoice] = useState<SubmitChoice>("anon");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const audioUrl = useMemo(() => URL.createObjectURL(audioBlob), [audioBlob]);
  useEffect(() => () => URL.revokeObjectURL(audioUrl), [audioUrl]);

  const canSubmit = choice === "anon" || (choice === "email" && isValidEmail(email.trim()));

  const handleSubmit = () => {
    if (choice === "email") {
      const err = email.trim() ? (isValidEmail(email.trim()) ? "" : "Please enter a valid email address.") : "Email address is required.";
      if (err) {
        setEmailError(err);
        return;
      }
    }
    onSubmit({ choice, email: choice === "email" ? email.trim() : undefined });
  };

  return (
    <div className="rec-form">
      {/* Preview */}
      <div>
        <p className="rec-preview-label">Review your recording:</p>
        <audio controls src={audioUrl} className="rec-audio" />
      </div>

      {/* Radio choice */}
      <fieldset className="rec-fieldset">
        <legend className="rec-legend">How would you like to submit?</legend>

        <label className="rec-radio-label">
          <input
            type="radio"
            name="submission-type"
            value="anon"
            checked={choice === "anon"}
            onChange={() => {
              setChoice("anon");
              setEmailError("");
            }}
            className="rec-radio"
          />
          <span>
            <span className="rec-radio__heading">Submit anonymously</span>
            <br />
            <span className="rec-radio__sub">You'll receive a private deletion code — your only way to remove your story later. Save it somewhere safe.</span>
          </span>
        </label>

        <label className="rec-radio-label rec-radio-label--last">
          <input type="radio" name="submission-type" value="email" checked={choice === "email"} onChange={() => setChoice("email")} className="rec-radio" />
          <span>
            <span className="rec-radio__heading">Submit with email</span>
            <br />
            <span className="rec-radio__sub">We'll send you a deep-link when your story is approved and live.</span>
          </span>
        </label>

        {choice === "email" && (
          <div style={{ marginTop: "0.75rem" }}>
            <input
              type="email"
              value={email}
              placeholder="your@email.com"
              className={`rec-input${emailError ? " is-error" : ""}`}
              onChange={(e) => {
                setEmail((e.target as HTMLInputElement).value);
                if (emailError && isValidEmail((e.target as HTMLInputElement).value.trim())) {
                  setEmailError("");
                }
              }}
              onBlur={() => {
                if (email.trim() && !isValidEmail(email.trim())) {
                  setEmailError("Please enter a valid email address.");
                }
              }}
            />
            {emailError && <p className="rec-input-error">{emailError}</p>}
          </div>
        )}
      </fieldset>

      {/* Actions */}
      <div className="rec-actions">
        <button onClick={onReRecord} className="rec-ghost-btn">
          ← Re-record
        </button>
        <button onClick={handleSubmit} disabled={!canSubmit} className={`rec-submit${canSubmit ? "" : " is-disabled"}`}>
          Submit story
        </button>
      </div>
    </div>
  );
}
